import { AuthResponse, LoginData, User, WritingStyle } from '../types';

const API_BASE_URL = '/api';

class ApiService {
  private token: string | null = null;

  constructor() {
    // Check if localStorage is available (client-side)
    if (typeof window !== 'undefined' && window.localStorage) {
      this.token = localStorage.getItem('token');
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (typeof window !== 'undefined' && window.localStorage) {
      if (token) {
        localStorage.setItem('token', token);
      } else {
        localStorage.removeItem('token');
      }
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Auth endpoints
  async login(data: LoginData): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    this.setToken(response.token);
    return response;
  }


  async getProfile(): Promise<User> {
    return this.request<User>('/auth/profile');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.request<void>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }


  logout() {
    this.setToken(null);
  }

  // Writing Styles endpoints
  async getWritingStyles(): Promise<WritingStyle[]> {
    return this.request<WritingStyle[]>('/writing-styles');
  }

  async createWritingStyle(data: { value: string; label: string; description?: string }): Promise<WritingStyle> {
    return this.request<WritingStyle>('/writing-styles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWritingStyle(id: string, data: { value: string; label: string; description?: string }): Promise<WritingStyle> {
    return this.request<WritingStyle>(`/writing-styles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteWritingStyle(id: string): Promise<void> {
    return this.request<void>(`/writing-styles/${id}`, {
      method: 'DELETE',
    });
  }
}

export const apiService = new ApiService();